import capitalize from 'lodash/capitalize';
import React from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Grid, Stack, Tag, TagProps, Text } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { graphql } from '@eluve/graphql.tada';

import { ArtifactHeader } from './ArtifactHeader';

export const patientConditionsFragment = graphql(`
  fragment PatientConditions on Patients @_unmask {
    __typename
    id
    conditions {
      __typename
      id
      severity
      recordedDate
      verificationStatus
      category
      code
      onsetDateTime
      note
    }
  }
`);

const ConditionBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) {
    return null;
  }

  const color = match<string, TagProps['color']>(status)
    .with('provisional', () => 'purple')
    .with('confirmed', () => 'orange')
    .otherwise(() => 'gray');

  return <Tag pill size="s" color={color} label={capitalize(status)} />;
};

export interface PatientConditionsProps {
  patientId: string;
}

export const PatientConditions: React.FC<PatientConditionsProps> = ({
  patientId,
}) => {
  const { conditions } = useCompleteFragment({
    fragment: patientConditionsFragment,
    key: { id: patientId },
  });

  return (
    <Stack gap={3}>
      <ArtifactHeader text="Conditions" />
      <Grid columns={1} gap={2} className="md:grid-cols-2 lg:grid-cols-3">
        {conditions.map((c) => (
          <Stack key={c.id} className="rounded-lg border px-2 py-4" gap={2}>
            <Stack>
              <Stack justify="between" gap={2} direction="horizontal">
                <Text size="md" weight="medium">
                  {c.code['text']}
                </Text>
                <ConditionBadge status={c.verificationStatus} />
              </Stack>
              <Text weight="regular">
                {formatDate.toISO(new Date(c.onsetDateTime ?? ''))}
              </Text>
            </Stack>
            <Text>{c.note}</Text>
          </Stack>
        ))}
      </Grid>
    </Stack>
  );
};
